import { z } from "zod";
import { apiClient } from "./api";

// --------------------------------------------------------------------------- //
// Shared cost-limit notice (chat + ingestion)                                 //
// --------------------------------------------------------------------------- //

/** Scope of an LLM cost-limit breach, mirroring the backend `detail.scope`. */
export type LlmCostScope = "per_user" | "global";

/** `detail.code` the backend sets on the HTTP 429 when the LLM spend cap is
 * met (`enforce_llm_cost_budget`). Shared so chat + ingestion match on the
 * same constant instead of duplicating the string literal. */
export const LLM_COST_LIMIT_CODE = "llm_cost_limit_exceeded";

/** Pull the breach scope out of a parsed error body shaped like
 * `{ detail: { code, scope, ... } }`. Returns `null` for any other error so
 * callers fall back to their generic handling. */
export function parseLlmCostLimitScope(payload: unknown): LlmCostScope | null {
  if (!payload || typeof payload !== "object" || !("detail" in payload)) return null;
  const detail = (payload as { detail: unknown }).detail;
  if (!detail || typeof detail !== "object") return null;
  const code = "code" in detail ? (detail as { code: unknown }).code : null;
  if (code !== LLM_COST_LIMIT_CODE) return null;
  const scope = "scope" in detail ? (detail as { scope: unknown }).scope : null;
  if (scope === "per_user" || scope === "global") return scope;
  return null;
}

/** User-facing notice for a cost-limit breach, worded distinctly per scope:
 * the per-user case is about *their* budget; the global case is about the
 * system's shared budget. */
export function llmCostLimitMessage(scope: LlmCostScope): string {
  if (scope === "per_user") {
    return "Alcanzaste tu límite de consumo de LLM para esta ventana. Espera a que se reinicie o pide a un administrador que ajuste tu tope.";
  }
  return "Se agotó el presupuesto de consumo de LLM del sistema para esta ventana. Pide a un administrador que ajuste el tope.";
}

/** Thrown by `streamChat` / ingestion helpers when a request is rejected by
 * the LLM cost limit, so the UI can render a scope-specific notice instead of
 * a raw error string. Its `message` is already the user-facing notice. */
export class LlmCostLimitError extends Error {
  readonly scope: LlmCostScope;

  constructor(scope: LlmCostScope) {
    super(llmCostLimitMessage(scope));
    this.name = "LlmCostLimitError";
    this.scope = scope;
  }
}

// --------------------------------------------------------------------------- //
// Admin CRUD for per-user overrides (`/admin/llm-cost-limits`)                //
// --------------------------------------------------------------------------- //

/** Upper bound for any USD cap the admin can type in (global window fields
 * in `rate-limits.tsx`, and the per-user override field on the user detail
 * page's Límites card). Shared so both surfaces validate against the same
 * ceiling. */
export const COST_LIMIT_USD_MAX = 100000;

/** Query key for the per-user override list (`GET /admin/llm-cost-limits`).
 * Shared by the rate-limits page (global settings) and the users list/detail
 * pages (the "límite" badge column and the Límites card) so all three read
 * and invalidate the same cache entry instead of drifting apart. */
export const LLM_COST_OVERRIDES_QUERY_KEY = ["admin", "llm-cost-limits"] as const;

// `limit_usd` stays a string end-to-end — the backend serializes the Decimal
// as a string to preserve precision; rendering parses it only for display.
export const userCostLimitSchema = z.object({
  user_id: z.string(),
  limit_usd: z.string(),
  updated_by: z.string(),
  updated_at: z.string(),
});
export type UserCostLimit = z.infer<typeof userCostLimitSchema>;

export const userCostLimitAuditSchema = z.object({
  user_id: z.string(),
  from_limit_usd: z.string().nullable(),
  to_limit_usd: z.string().nullable(),
  actor_id: z.string(),
  created_at: z.string(),
});
export type UserCostLimitAudit = z.infer<typeof userCostLimitAuditSchema>;

const userCostLimitListSchema = z.array(userCostLimitSchema);
const userCostLimitAuditListSchema = z.array(userCostLimitAuditSchema);
const VOID = z.unknown().transform(() => undefined);

// Read-only standing of the global cap for the active calendar period. Money
// fields stay strings (Decimal precision); `spent_usd`/`remaining_usd` are null
// when usage can't be read (fail open) or there is no cap. Admin-only.
export const globalCostStatusSchema = z.object({
  enabled: z.boolean(),
  period: z.enum(["daily", "weekly", "monthly"]),
  tz: z.string(),
  cap_usd: z.string(),
  spent_usd: z.string().nullable(),
  remaining_usd: z.string().nullable(),
  period_start: z.string(),
  resets_at: z.string(),
});
export type GlobalCostStatus = z.infer<typeof globalCostStatusSchema>;

export const llmCostLimitsApi = {
  list: () => apiClient.get("/admin/llm-cost-limits", userCostLimitListSchema),
  set: (userId: string, limitUsd: number) =>
    apiClient.put(`/admin/llm-cost-limits/${userId}`, userCostLimitSchema, {
      limit_usd: limitUsd,
    }),
  // 204 on success; 404 `{code:"no_override"}` when there is no row to clear.
  clear: (userId: string) => apiClient.delete(`/admin/llm-cost-limits/${userId}`, VOID),
  audit: (params: { limit?: number; offset?: number } = {}) =>
    apiClient.get("/admin/llm-cost-limits/audit", userCostLimitAuditListSchema, {
      query: { limit: params.limit ?? 50, offset: params.offset ?? 0 },
    }),
  globalStatus: () => apiClient.get("/admin/llm-cost-limits/global-status", globalCostStatusSchema),
};
